import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import { LangProvider, useLang } from "./lib/i18n";
import Admin from "./pages/Admin";
import { Nav } from "./sections/Nav";
import { Hero } from "./sections/Hero";
import { Ribbon } from "./sections/Ribbon";
import { Welcome } from "./sections/Welcome";
import { Story } from "./sections/Story";
import { WeddingParty } from "./sections/WeddingParty";
import { Gallery } from "./sections/Gallery";
import { Weekend } from "./sections/Weekend";
import { Venue, Travel } from "./sections/Venue";
import { Rsvp } from "./sections/Rsvp";
import { Faq, Registry } from "./sections/FaqRegistry";
import { Footer } from "./sections/Footer";
import { RsvpBanner } from "./components/RsvpBanner";
import { PageHeader } from "./components/PageHeader";

const TONES = {
  story: { tone: "#33431A", ghost: "Amor", accent: "#F4B8C6" },
  party: { tone: "#6B2A3A", ghost: "Familia", accent: "#F4B8C6" },
  weekend: { tone: "#2C3552", ghost: "Fiesta", accent: "#E9B84C" },
  venue: { tone: "#26301B", ghost: "Loews", accent: "#E9B84C" },
  travel: { tone: "#1E4B45", ghost: "Miami", accent: "#E9B84C" },
  faq: { tone: "#8A4532", ghost: "?", accent: "#F4B8C6" },
  registry: { tone: "#96475A", ghost: "Gracias", accent: "#E9B84C" },
};

const EXPLORE = [
  ["story", "/story"],
  ["party", "/wedding-party"],
  ["weekend", "/weekend"],
  ["venue", "/venue"],
  ["travel", "/travel"],
  ["faq", "/faq"],
];

const ExploreGrid = () => {
  const { c } = useLang();
  return (
    <section className="px-6 pb-28 sm:pb-36" data-testid="explore-grid">
      <div className="max-w-5xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {EXPLORE.map(([id, path]) => (
            <Link
              key={id}
              to={path}
              data-testid={`explore-${id}`}
              style={{ borderLeft: `4px solid ${TONES[id]?.tone || "#2C3552"}` }}
              className="group flex items-center justify-between border border-[#DFE4D0] bg-white/50 px-7 py-8 hover:border-[#E15A70] hover:-translate-y-1 transition-[border-color,transform] duration-300"
            >
              <span className="font-serif-display text-2xl font-medium">{c.nav[id]}</span>
              <ArrowRight size={18} className="text-[#E15A70] group-hover:translate-x-1.5 transition-transform duration-300" />
            </Link>
          ))}
        </div>
        <Link
          to="/rsvp"
          data-testid="explore-rsvp"
          className="group mt-4 flex items-center justify-between bg-[#2C3552] text-[#FAFAF1] px-7 py-8 hover:bg-[#E15A70] transition-colors duration-300"
        >
          <span className="font-serif-display text-2xl font-medium">{c.nav.rsvp} — {c.rsvp.deadlineDate}</span>
          <ArrowRight size={18} className="text-[#F4B8C6] group-hover:translate-x-1.5 group-hover:text-[#FAFAF1] transition-[transform,color] duration-300" />
        </Link>
      </div>
    </section>
  );
};

const Page = ({ header, children }) => {
  const { c } = useLang();
  return (
    <motion.main
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="pt-16"
    >
      {header && <PageHeader {...TONES[header]} title={c.nav[header]} />}
      {children}
    </motion.main>
  );
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    lenisRef.current = lenis;
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="grain bg-[#FAFAF1] text-[#2C3552] min-h-screen flex flex-col">
      <Nav />
      <div className="flex-1">{children}</div>
      <Ribbon dark />
      <Footer />
      <RsvpBanner onRsvp={() => navigate("/rsvp")} />
    </div>
  );
};

function App() {
  return (
    <LangProvider>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route
            path="*"
            element={
              <Layout>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
                        <Hero />
                        <Ribbon />
                        <Welcome />
                        <ExploreGrid />
                      </motion.main>
                    }
                  />
                  <Route path="/story" element={<Page header="story"><Story /><Gallery /></Page>} />
                  <Route path="/wedding-party" element={<Page header="party"><WeddingParty /></Page>} />
                  <Route path="/weekend" element={<Page header="weekend"><Weekend /></Page>} />
                  <Route path="/venue" element={<Page header="venue"><Venue /></Page>} />
                  <Route path="/travel" element={<Page header="travel"><Travel /></Page>} />
                  <Route path="/rsvp" element={<Page><Rsvp /></Page>} />
                  <Route path="/faq" element={<Page header="faq"><Faq /></Page>} />
                  <Route path="/registry" element={<Page header="registry"><Registry /></Page>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
    </LangProvider>
  );
}

export default App;
